// Remove direct socket dependency at load time to prevent circular dependency
// import { socketService } from './socket';

export interface QueuedOperation {
  id: string;
  event: string;
  args: any[];
  timestamp: number;
  retryCount: number;
}

const STORAGE_KEY = "braindog_offline_queue";
const MAX_RETRIES = 5;

// List of operations that are safe/important to queue offline
const OFFLINE_SUPPORTED_EVENTS = [
  "update-story-notes",
  "update-event-notes",
  "new-log",
  "update-stories",
  "update-story-status",
  "delete-story",
];

class OfflineSync {
  private queue: QueuedOperation[] = [];
  private isProcessing = false;
  private socketRef: any = null; // Hold reference injected at runtime

  constructor() {
    this.loadQueue();
  }

  // Call this after socketService is initialized to break circular dependencies
  public init(socketServiceInstance: any) {
    if (this.socketRef) return;
    this.socketRef = socketServiceInstance;

    // Listen to socket connection events to trigger sync
    this.socketRef.onConnectionChange((connected: boolean) => {
      if (connected && this.queue.length > 0) {
        console.info(
          `[OfflineSync] 🌐 Network restored. Processing ${this.queue.length} pending operations...`,
        );
        this.processQueue();
      }
    });
  }

  private loadQueue() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.queue = JSON.parse(stored);
        console.info(`[OfflineSync] 📦 Loaded ${this.queue.length} operations from local storage.`);
      }
    } catch (error) {
      console.error("[OfflineSync] Failed to load offline queue:", error);
      this.queue = [];
    }
  }

  private saveQueue() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.queue));

      // Dispatch custom event for UI updates (e.g. showing a cloud icon with pending count)
      window.dispatchEvent(
        new CustomEvent("offline-sync-updated", {
          detail: { count: this.queue.length },
        }),
      );
    } catch (error) {
      console.error("[OfflineSync] Failed to save offline queue:", error);
    }
  }

  public isEventSupported(event: string): boolean {
    return OFFLINE_SUPPORTED_EVENTS.includes(event);
  }

  public queueOperation(event: string, args: any[]) {
    // Fallback ID generator in case crypto is not available in non-HTTPS local networks
    const generateId = () => {
      if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
      }
      return Date.now().toString(36) + Math.random().toString(36).substring(2);
    };

    // `update-stories` payloads are full snapshots, so the latest one
    // supersedes any earlier queued snapshot. Without this, replay on
    // reconnect re-sends stale snapshots that re-insert logs the user
    // since deleted, producing "duplicates appearing after navigation".
    if (event === "update-stories") {
      this.queue = this.queue.filter((op) => op.event !== "update-stories");
    }

    const operation: QueuedOperation = {
      id: generateId(),
      event,
      args,
      timestamp: Date.now(),
      retryCount: 0,
    };

    this.queue.push(operation);
    this.saveQueue();
    console.info(
      `[OfflineSync] 📥 Queued operation: ${event} (Total pending: ${this.queue.length})`,
    );
  }

  public async processQueue() {
    if (!this.socketRef) {
      console.warn("[OfflineSync] processQueue called before init()");
      return;
    }

    if (this.isProcessing || this.queue.length === 0 || !this.socketRef.connected) {
      return;
    }

    this.isProcessing = true;

    // Wait until select-project has been re-acked on this connection so the
    // server has socket.projectId set. Otherwise replayed update-stories /
    // new-log race ahead of select-project and get rejected with
    // "No projectId (undefined)".
    if (typeof this.socketRef.waitForProjectReady === "function") {
      await this.socketRef.waitForProjectReady();
      if (!this.socketRef.connected) {
        this.isProcessing = false;
        return;
      }
    }

    // Process a copy of the queue to handle items sequentially safely
    const currentQueue = [...this.queue];
    const successfulIds: string[] = [];

    for (const op of currentQueue) {
      try {
        if (!this.socketRef.connected) {
          console.warn("[OfflineSync] Network dropped during sync. Halting.");
          break;
        }

        console.info(`[OfflineSync] 📤 Syncing operation: ${op.event} (Retry ${op.retryCount})`);

        // We emit without waiting for generic ACK since many endpoints don't use standard acks.
        // The socket layer will deliver the message reliably if connected.
        this.socketRef.rawEmit(op.event, ...op.args);

        successfulIds.push(op.id);

        // Small delay to prevent flooding the server
        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`[OfflineSync] Error syncing operation ${op.event}:`, error);

        // Find and update the original operation in the queue
        const originalOp = this.queue.find((q) => q.id === op.id);
        if (originalOp) {
          originalOp.retryCount++;
          if (originalOp.retryCount >= MAX_RETRIES) {
            console.error(`[OfflineSync] Operation ${op.event} exceeded max retries. Dropping.`);
            successfulIds.push(op.id); // Remove from queue by pretending success
          }
        }
      }
    }

    // Remove successful operations from the real queue
    if (successfulIds.length > 0) {
      this.queue = this.queue.filter((op) => !successfulIds.includes(op.id));
      this.saveQueue();
    }

    this.isProcessing = false;

    if (this.queue.length > 0 && this.socketRef.connected) {
      // If there are still items (maybe added during sync), run again
      setTimeout(() => this.processQueue(), 1000);
    } else if (successfulIds.length > 0) {
      console.info(`[OfflineSync] ✨ Sync complete.`);
    }
  }

  public getPendingCount(): number {
    return this.queue.length;
  }

  // Test method to force clear queue if needed
  public clearQueue() {
    this.queue = [];
    this.saveQueue();
  }
}

export const offlineSyncService = new OfflineSync();
